import { useState, useEffect } from 'react';
import { VacationPeriod, AlternativeSlot } from '../types';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAdminDispatch } from '../context/AdminContext';

export const useVacation = () => {
  const [vacationPeriods, setVacationPeriods] = useState<VacationPeriod[]>([]);
  const [selectedVacationLocation, setSelectedVacationLocation] = useState<number | null>(null);
  const adminDispatch = useAdminDispatch();

  // Initial laden
  useEffect(() => {
    (async () => {
      if (!isSupabaseConfigured) return;
      const { data, error } = await supabase
        .from('vacation_periods')
        .select('*')
        .order('start_date');
      if (!error && data) {
        const mapped = data.map((v: any) => ({
          id: Number(v.id),
          location_id: Number(v.location_id),
          title: v.title,
          startDate: v.start_date,
          endDate: v.end_date,
          reason: v.reason,
          isActive: v.is_active,
          createdAt: v.created_at
        })) as VacationPeriod[];
        setVacationPeriods(mapped);
        adminDispatch({ type: 'SET_VACATION_PERIODS', payload: mapped });
      }
    })();
  }, []);

  const handleAddVacation = async (vacationData: Omit<VacationPeriod, 'id' | 'createdAt'>) => {
    if (isSupabaseConfigured) {
      const { data, error } = await supabase
        .from('vacation_periods')
        .insert({
          location_id: vacationData.location_id,
          title: vacationData.title,
          start_date: vacationData.startDate,
          end_date: vacationData.endDate,
          reason: vacationData.reason,
          is_active: vacationData.isActive
        })
        .select()
        .single();
      if (error) throw error;
      const newVacation: VacationPeriod = {
        id: data.id,
        location_id: data.location_id,
        title: data.title,
        startDate: data.start_date,
        endDate: data.end_date,
        reason: data.reason,
        isActive: data.is_active,
        createdAt: data.created_at
      };
      setVacationPeriods(prev => [...prev, newVacation]);
      adminDispatch({ type: 'SET_VACATION_PERIODS', payload: [...vacationPeriods, newVacation] });
      return;
    }
    const newVacation: VacationPeriod = {
      id: Date.now(),
      ...vacationData,
      createdAt: new Date().toISOString()
    };
    setVacationPeriods(prev => [...prev, newVacation]);
  };

  const handleDeleteVacation = async (vacationId: number) => {
    if (isSupabaseConfigured) {
      await supabase.from('vacation_periods').delete().eq('id', vacationId);
    }
    setVacationPeriods(prev => {
      const updated = prev.filter(v => v.id !== vacationId);
      adminDispatch({ type: 'SET_VACATION_PERIODS', payload: updated });
      return updated;
    });
  };

  const generateAlternativeSlots = (originalDate: Date, locationId: number): AlternativeSlot[] => {
    const alternatives: AlternativeSlot[] = [];
    const startDate = new Date(originalDate);
    startDate.setDate(startDate.getDate() - 7);

    for (let i = 0; i < 14; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      // Füge verschiedene Zeitslots für diesen Tag hinzu
      ['09:00', '11:00', '14:00', '16:00'].forEach(time => {
        alternatives.push({
          date: date.toISOString().split('T')[0],
          time,
          location_id: locationId,
          available: !isDateInVacation(date, locationId)
        });
      });
    }

    return alternatives;
  };

  const isDateInVacation = (date: Date, locationId: number): boolean => {
    return vacationPeriods.some(vacation => {
      if (vacation.location_id !== locationId) return false;
      
      const start = new Date(vacation.startDate);
      const end = new Date(vacation.endDate);
      return date >= start && date <= end;
    });
  };

  const getVacationPeriod = (date: Date, locationId: number): VacationPeriod | null => {
    return vacationPeriods.find(vacation => 
      vacation.location_id === locationId && 
      date >= new Date(vacation.startDate) && 
      date <= new Date(vacation.endDate)
    ) || null;
  };

  return {
    vacationPeriods,
    setVacationPeriods,
    selectedVacationLocation,
    setSelectedVacationLocation,
    handleAddVacation,
    handleDeleteVacation,
    generateAlternativeSlots,
    isDateInVacation,
    getVacationPeriod
  };
}; 